const warned = new Set<string>();

/**
 * Dev-only advice about a misuse the types allow. Keyed per offender rather than behind one
 * module-level flag, so a page wired wrongly in five places names all five — React's own
 * controlled-input warning uses a single flag and reports only whichever field rendered first.
 */
export const devWarning = (key: string, message: string): void => {
    if (process.env.NODE_ENV === 'production' || warned.has(key)) {
        return;
    }
    warned.add(key);
    console.warn(`malachite: ${message}`);
};

/** Test-only: the warnings are once-per-offender for a process, which spec isolation is not. */
export const resetDevWarnings = (): void => warned.clear();

/**
 * The value ladder every form control paints from: an explicit `value` wins, then the value
 * the form holds, then `defaultValue`, then empty. Matches what react-hook-form's own
 * `useController` computes, so a bound control and the resolver never disagree about the field.
 */
export const paintedFieldValue = ({
    component,
    name,
    value,
    formValue,
    defaultValue,
    bound,
    uncontrolledValue,
}: {
    component: string;
    name: string;
    value?: string;
    formValue: unknown;
    defaultValue?: string;
    bound: boolean;
    uncontrolledValue: string;
}): string => {
    if (value !== undefined) {
        return value;
    }
    if (!bound) {
        return uncontrolledValue;
    }
    if (defaultValue !== undefined) {
        devWarning(
            `${component}:${name}:defaultValue`,
            `\`defaultValue\` will not work on a field bound to a form. Use \`useForm({ defaultValues: { ${name}: … } })\` instead. (${component} \`${name}\`)`,
        );
    }
    if (formValue === undefined || formValue === null) {
        return defaultValue ?? '';
    }
    if (typeof formValue !== 'string') {
        devWarning(
            `${component}:${name}:nonString`,
            `field \`${name}\` holds a ${typeof formValue}, but a text control paints and reports strings — the first keystroke will store a string. Convert it in the form (\`valueAsNumber\`, or a coercing schema). (${component} \`${name}\`)`,
        );
    }
    return String(formValue);
};
